import { z } from 'zod'

// Contact form schema
export const contactSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
})

// Prayer request schema
export const prayerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  prayerRequest: z.string().min(10, 'Please describe your prayer request'),
  isConfidential: z.boolean().default(false),
})

// Visit registration schema
export const visitSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  visitDate: z.string().min(1, 'Please select a date'),
  serviceTime: z.string().min(1, 'Please select a service time'),
  familySize: z.string().min(1, 'Please indicate family size'),
  childrenAges: z.string().optional(),
  howHeard: z.string().min(1, 'Please tell us how you heard about us'),
  questions: z.string().optional(),
})

// Event RSVP schema
export const rsvpSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  attendees: z.coerce.number().min(1, 'At least 1 attendee required').max(20, 'Maximum 20 attendees'),
  notes: z.string().optional(),
})

// Newsletter schema
export const newsletterSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  name: z.string().optional(),
})

// Salvation decision schema
export const salvationSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  decision: z.enum(['first_time', 'rededication', 'baptism', 'membership']),
  wantContact: z.boolean().default(true),
})

// Connect Card (new visitor) schema — short & easy
export const connectCardSchema = z.object({
  firstName: z.string().min(2, 'First name is required'),
  lastName: z.string().min(2, 'Last name is required'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional(),
  serviceAttended: z.string().min(1, 'Please tell us which service you attended'),
  visitorType: z.enum(['first_time', 'returning']),
  interests: z.array(z.string()).optional().default([]),
  message: z.string().optional(),
})

// Blog post schema
export const blogPostSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  slug: z.string().min(3, 'Slug must be at least 3 characters'),
  excerpt: z.string().min(10, 'Excerpt helps preview the post (10+ chars)'),
  body: z.string().min(20, 'Post body must be at least 20 characters'),
  coverImageUrl: z.string().optional(),
  author: z.string().min(2, 'Author is required'),
  category: z.string().min(1, 'Please choose a category'),
  tags: z.array(z.string()).optional().default([]),
  isPublished: z.boolean().default(false),
})

// Admin schemas
export const heroSlideSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  subtitle: z.string().optional(),
  imageUrl: z.string().url('Please enter a valid URL').optional(),
  videoUrl: z.string().url('Please enter a valid URL').optional(),
  ctaText: z.string().optional(),
  ctaLink: z.string().optional(),
  order: z.coerce.number().default(0),
  isActive: z.boolean().default(true),
})

export const eventSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  date: z.string().min(1, 'Date is required'),
  time: z.string().min(1, 'Time is required'),
  endTime: z.string().optional(),
  location: z.string().min(1, 'Location is required'),
  address: z.string().optional(),
  imageUrl: z.string().optional(),
  category: z.string().default('general'),
  capacity: z.coerce.number().optional(),
  requiresRSVP: z.boolean().default(false),
  isPublished: z.boolean().default(true),
})

export const sermonSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  speaker: z.string().min(2, 'Speaker name is required'),
  series: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
  youtubeUrl: z.string().url('Please enter a valid YouTube URL'),
  description: z.string().optional(),
  scriptures: z.string().optional(),
  notesUrl: z.string().optional(),
  thumbnailUrl: z.string().optional(),
  isPublished: z.boolean().default(true),
})

export const ministrySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  category: z.string().min(1, 'Category is required'),
  leaderId: z.string().optional(),
  leaderName: z.string().optional(),
  schedule: z.string().optional(),
  location: z.string().optional(),
  imageUrl: z.string().optional(),
  isActive: z.boolean().default(true),
})

export const teamMemberSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  role: z.string().min(2, 'Role is required'),
  bio: z.string().optional(),
  imageUrl: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  phone: z.string().optional(),
  order: z.coerce.number().default(0),
  isPublished: z.boolean().default(true),
})

export const siteSettingsSchema = z.object({
  churchName: z.string().min(1, 'Church name is required'),
  tagline: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().email().optional().or(z.literal('')),
  address: z.object({
    street: z.string(),
    city: z.string(),
    state: z.string(),
    zip: z.string(),
  }).optional(),
  officeHours: z.string().optional(),
  footerText: z.string().optional(),
  metaDescription: z.string().optional(),
  metaKeywords: z.string().optional(),
})

// Workers portal schemas
export const workerRegistrationSchema = z.object({
  displayName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
  departmentInterests: z.array(z.string()).default([]),
  smsConsent: z.boolean().default(false),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

export const departmentSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  description: z.string().optional(),
  leaderName: z.string().optional(),
  order: z.coerce.number().default(0),
  isActive: z.boolean().default(true),
})

export const announcementSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  body: z.string().min(10, 'Announcement must be at least 10 characters'),
  departmentIds: z.array(z.string()).default([]),
  pinned: z.boolean().default(false),
  isPublished: z.boolean().default(true),
})

export const assignmentSchema = z.object({
  date: z.string().min(1, 'Date is required'),
  serviceName: z.string().min(2, 'Service name is required'),
  serviceTime: z.string().optional(),
  departmentId: z.string().min(1, 'Department is required'),
  workerUid: z.string().min(1, 'Worker is required'),
  duty: z.string().optional(),
  notes: z.string().optional(),
})

export const resourceSchema = z.object({
  title: z.string().min(2, 'Title must be at least 2 characters'),
  description: z.string().optional(),
  type: z.enum(['file', 'link']),
  url: z.string().optional(),
  departmentIds: z.array(z.string()).default([]),
})

// Helper to validate form data
export const validateForm = (schema, data) => {
  try {
    const result = schema.parse(data)
    return { success: true, data: result, errors: null }
  } catch (error) {
    if (error instanceof z.ZodError) {
      const errors = {}
      error.errors.forEach((err) => {
        const path = err.path.join('.')
        errors[path] = err.message
      })
      return { success: false, data: null, errors }
    }
    throw error
  }
}
